import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Sprout, TrendingUp, AlertTriangle, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import DashboardSidebar from '@/components/DashboardSidebar';
import FarmOverview from '@/components/FarmOverview';
import FarmCalendar from '@/components/FarmCalendar';
import QuickStats from '@/components/QuickStats';
import CompostManagement from '@/components/CompostManagement';
import ProductionDashboard from '@/components/ProductionDashboard';
import ActualProductionView from '@/components/ActualProductionView';
import LogHarvestCard from '@/components/LogHarvestCard';
import WateringScheduleManager from '@/components/WateringScheduleManager';
import PopupAlertSystem from '@/components/PopupAlertSystem';
import CycleManagement from '@/components/CycleManagement';
import RoomAnalytics from '@/components/RoomAnalytics';
import ForecastDashboard from '@/components/ForecastDashboard';
import TodayActionTasks from '@/components/TodayActionTasks';
import TaskPhotoThumb from '@/components/TaskPhotoThumb';
import CycleConfig from '@/components/CycleConfig';
import RawMaterials from '@/components/RawMaterials';
import WateringTodayStatus from '@/components/WateringTodayStatus';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Delays View Component
const DelaysView = () => {
  const [delays, setDelays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDelays = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/room-cycles?has_delay=true`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        // Handle both array and object with 'cycles' key response formats
        const cyclesData = Array.isArray(res.data) ? res.data : (res.data.cycles || []);
        setDelays(cyclesData.filter(c => c.has_delay));
      } catch (error) {
        // Silent fail - delays UI will show empty state
      } finally {
        setLoading(false);
      }
    };
    fetchDelays();
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading delays...</div>;

  if (delays.length === 0) {
    return (
      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="py-12 text-center">
          <AlertTriangle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-green-800">No Delays</h3>
          <p className="text-sm text-green-600 mt-1">All cycles are running on schedule.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-900">Delayed Cycles ({delays.length})</h2>
      {delays.map((cycle) => (
        <Card key={cycle.id} className="border-red-200 bg-red-50/50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-red-900">{cycle.batch_name}</p>
                <p className="text-sm text-red-700">Phase: {cycle.current_phase}</p>
                <p className="text-sm text-red-600 mt-1">Reason: {cycle.delay_reason || 'No reason provided'}</p>
              </div>
              <p className="text-sm text-red-700">Day {cycle.current_day}/45</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Logs View Component
const LogsTimestampView = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const token = localStorage.getItem('token');
        const [taskLogs, prodLogs] = await Promise.all([
          axios.get(`${API}/room-cycles/task-logs?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/production`, { headers: { Authorization: `Bearer ${token}` } })
        ]);

        // /api/production returns { records, total, total_weight_kg }; /api/room-cycles/task-logs returns array
        const taskArr = Array.isArray(taskLogs.data) ? taskLogs.data : (taskLogs.data.logs || taskLogs.data.records || []);
        const prodArr = Array.isArray(prodLogs.data) ? prodLogs.data : (prodLogs.data.records || []);

        const combined = [
          ...taskArr.map(l => ({ ...l, type: 'task', timestamp: l.completed_at })),
          ...prodArr.map(l => ({ ...l, type: 'production', timestamp: l.created_at || l.harvest_date }))
        ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 50);

        setLogs(combined);
      } catch (error) {
        console.error('Failed to fetch activity logs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading logs...</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-900">Activity Logs</h2>
      <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="activity-logs-list">
        {logs.length === 0 ? (
          <p className="text-gray-500 text-sm">No activity logged yet.</p>
        ) : logs.map((log) => (
          <Card key={`${log.type}-${log.id || log.timestamp}`} className="border-gray-100">
            <CardContent className="py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">
                    {log.type === 'task'
                      ? `${log.task || 'Task'}${log.phase ? ` - ${log.phase}` : ''}${log.room_name ? ` · ${log.room_name}` : ''}`
                      : `Production: ${log.weight_kg}kg${log.room_name ? ` · ${log.room_name}` : ''}${log.batch_name ? ` · ${log.batch_name}` : ''}`}
                  </p>
                  <p className="text-sm text-gray-500">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                    {log.logged_by_name && ` · by ${log.logged_by_name}`}
                  </p>
                  {log.notes && <p className="text-xs text-gray-600 mt-1">{log.notes}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {log.photo_id && <TaskPhotoThumb photoId={log.photo_id} size="w-12 h-12" />}
                  <span className={`text-xs px-2 py-1 rounded ${log.type === 'task' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                    {log.type}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const GrowerDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const statsRes = await axios.get(`${API}/stats/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-4">
            <LogHarvestCard userRole="grower" />
            <FarmOverview />
          </div>
        );
      case 'room-tasks':
        return <TodayActionTasks showHarvest={true} compact={false} />;
      case 'forecast':
        return <ForecastDashboard userRole="grower" />;
      case 'production':
        return <ProductionDashboard userRole="grower" />;
      case 'actual-production':
        return <ActualProductionView userRole="grower" currentUserId={user?.id} />;
      case 'rooms':
        return <CycleManagement />;
      case 'cycle-config':
        return <CycleConfig />;
      case 'materials':
        return <RawMaterials />;
      case 'watering':
        return (
          <div className="space-y-4">
            <WateringTodayStatus />
            <WateringScheduleManager />
          </div>
        );
      case 'composting':
        return <CompostManagement showBatchReminders={false} showReadyForSpawning={false} />;
      case 'analytics':
        return <RoomAnalytics showProductionForecast={false} />;
      case 'calendar':
        return <FarmCalendar />;
      case 'delays':
        return <DelaysView />;
      case 'logs':
        return <LogsTimestampView />;
      default:
        return (
          <div className="space-y-4">
            <LogHarvestCard userRole="grower" />
            <FarmOverview />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAF9]">
      <ImpersonationBanner role={user?.role} />
      {/* Pop-up Alert System */}
      <PopupAlertSystem />
      
      {/* Sidebar */}
      <DashboardSidebar 
        role="grower" 
        activeTab={activeTab} 
        onTabChange={setActiveTab} 
      />
      
      {/* Main Content - with left margin for sidebar on desktop */}
      <div className="md:ml-16 transition-all duration-300">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 ml-12 md:ml-0">
                <Sprout className="w-6 h-6 md:w-8 md:h-8 text-[#1A4D2E]" />
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>A3ROS</h1>
                  <p className="text-xs md:text-sm text-gray-500 hidden sm:block">Grower Dashboard</p>
                </div>
              </div>
              <div className="flex items-center gap-2 md:gap-4">
                <span className="text-xs md:text-sm text-gray-600 hidden sm:inline">Welcome, {user.full_name}</span>
                <Button
                  data-testid="logout-button"
                  onClick={onLogout}
                  variant="outline"
                  size="sm"
                  className="border-gray-300 hover:bg-gray-100 text-xs md:text-sm"
                >
                  <LogOut className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 md:py-8">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading dashboard...</div>
          ) : (
            <>
              {/* Stats Grid - Responsive */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8" data-testid="stats-grid">
                <Card className="border-gray-100 shadow-sm">
                  <CardHeader className="pb-2 md:pb-3 p-3 md:p-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center gap-1 md:gap-2">
                      <Sprout className="w-3 h-3 md:w-4 md:h-4" />
                      <span className="hidden sm:inline">Total</span> Rooms
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 md:p-6 pt-0">
                    <div className="text-2xl md:text-3xl font-bold text-gray-900">{stats?.total_rooms || 0}</div>
                    <p className="text-xs text-gray-500 mt-1">{stats?.active_rooms || 0} active</p>
                  </CardContent>
                </Card>

                <Card className="border-gray-100 shadow-sm">
                  <CardHeader className="pb-2 md:pb-3 p-3 md:p-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center gap-1 md:gap-2">
                      <Package className="w-3 h-3 md:w-4 md:h-4" />
                      <span className="hidden sm:inline">Today's</span> Harvest
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 md:p-6 pt-0">
                    <div className="text-2xl md:text-3xl font-bold text-gray-900">{stats?.today_harvest_kg || 0} kg</div>
                    <p className="text-xs text-gray-500 mt-1">Daily</p>
                  </CardContent>
                </Card>

                <Card className="border-gray-100 shadow-sm">
                  <CardHeader className="pb-2 md:pb-3 p-3 md:p-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-gray-600 flex items-center gap-1 md:gap-2">
                      <TrendingUp className="w-3 h-3 md:w-4 md:h-4" />
                      <span className="hidden sm:inline">Month's</span> Total
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 md:p-6 pt-0">
                    <div className="text-2xl md:text-3xl font-bold text-gray-900">{stats?.month_harvest_kg || 0} kg</div>
                    <p className="text-xs text-gray-500 mt-1">30 days</p>
                  </CardContent>
                </Card>

                <Card className="border-gray-100 shadow-sm bg-red-50/30 border-red-200">
                  <CardHeader className="pb-2 md:pb-3 p-3 md:p-6">
                    <CardTitle className="text-xs md:text-sm font-medium text-red-700 flex items-center gap-1 md:gap-2">
                      <AlertTriangle className="w-3 h-3 md:w-4 md:h-4" />
                      Delayed
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 md:p-6 pt-0">
                    <div className="text-2xl md:text-3xl font-bold text-red-600">{stats?.delayed_cycles || 0}</div>
                    <p className="text-xs text-red-600 mt-1">Attention</p>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Stats Panel */}
              <div className="mb-6 md:mb-8">
                <QuickStats />
              </div>

              {/* Dynamic Content based on sidebar selection */}
              <div data-testid="dashboard-content">
                {renderContent()}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default GrowerDashboard;
